import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';

/**
 * Gates the Control Center on holding a platform role.
 *
 * This is a CONVENIENCE, not the security boundary. Roles here come from the
 * stored session, which the client could edit; the real guard is `platformOnly`
 * on the server, which re-resolves roles from the database on every request and
 * would refuse the API calls regardless. What this does is stop someone who has
 * no business in the portal from reaching a shell full of failing requests.
 *
 * Kept as its own component rather than a flag on RequireAuth because the two
 * answer different questions — "are you signed in" and "are you staff" — and
 * merging them makes the second easy to lose sight of.
 */

const PLATFORM_ROLES = [
  'super_admin', 'operations_admin', 'finance_admin', 'support_admin',
  'marketplace_admin', 'content_admin', 'readonly_admin',
];

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { isAuthenticated, roles } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Signed in, but not staff: send them to their own dashboard rather than to
  // sign-in, which would look like their session had expired.
  if (!roles.some(r => PLATFORM_ROLES.includes(r))) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
